import { useMutation, useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { Link } from 'react-router'

import HighlightableContent from '@/components/misc/HighlightableContent'
import MutationIcon from '@/components/misc/MutationIcon'
import SanitizedHtml from '@/components/misc/SanitizedHtml'
import Select from '@/components/select/Select'
import { useInvalidateQueries } from '@/hooks/useInvalidateQueries'
import { getCssVariable } from '@/lib/helpers/domHelpers'
import { QUERY_KEYS } from '@/lib/queryKeys'
import { ROUTES } from '@/lib/routes'
import companyUserService from '@/lib/services/companyUserService'
import locationService from '@/lib/services/locationService'
import { cn } from '@/lib/utils'
import { MutationIconState } from '@/types/misc'
import { LocationItem } from '@/types/responses/locations'

interface LocationOption {
  label: string
  value: string
}

function locationToOption(location: LocationItem): LocationOption {
  return {
    label: location.name,
    value: location['@id'],
  }
}

interface Props {
  companyUserId?: number
  currentLocation?: LocationItem | null
  readOnly: boolean
  value?: LocationItem | null
  onChange?: (location: LocationItem | null) => void
}

const SelectLocation: React.FC<Props> = ({
  companyUserId = 0,
  currentLocation = null,
  readOnly,
  value,
  onChange,
}) => {
  const isControlled = onChange !== undefined
  const invalidateQueries = useInvalidateQueries()
  const [mutationIconState, setMutationIconState] = useState({
    state: MutationIconState.ok,
    message: '',
  })

  const { data: locationsData } = useQuery({
    queryKey: QUERY_KEYS.locations(),
    queryFn: () => locationService.getLocations(),
    staleTime: 60_000,
  })

  const options: LocationOption[] =
    locationsData?.['hydra:member'].map(locationToOption) ?? []

  const patchMutation = useMutation({
    mutationFn: (locationIri: IRI | null) =>
      companyUserService.updateLocation(companyUserId, locationIri),
    onMutate: () => {
      setMutationIconState({ state: MutationIconState.mutating, message: '' })
    },
    onSuccess: () => {
      setMutationIconState({ state: MutationIconState.success, message: '' })
      invalidateQueries([
        QUERY_KEYS.companyUsers(),
        QUERY_KEYS.referrals(),
        // The old and new locations' user counts and users.
        QUERY_KEYS.locations(),
      ])
    },
    onError: () => {
      setMutationIconState({
        state: MutationIconState.error,
        message: 'Failed to update location',
      })
    },
  })

  const effectiveCurrentLocation = isControlled
    ? (value ?? null)
    : currentLocation
  const selectedOption = effectiveCurrentLocation
    ? locationToOption(effectiveCurrentLocation)
    : null

  const isPending = isControlled ? false : patchMutation.isPending

  function handleChange(option: LocationOption | null) {
    if (isControlled) {
      const locationItem = option
        ? (locationsData?.['hydra:member'].find(
            (location) => location['@id'] === option.value,
          ) ?? null)
        : null

      if (onChange) {
        onChange(locationItem)
      }
    } else {
      patchMutation.mutate(option ? option.value : null)
    }
  }

  if (readOnly) {
    return (
      <span className="text-sm">{effectiveCurrentLocation?.name ?? '—'}</span>
    )
  }

  return (
    <div className="flex items-center">
      <Select<LocationOption>
        className={cn('w-full', { 'opacity-50': isPending })}
        isDisabled={isPending}
        isClearable
        placeholder="Select..."
        options={options}
        value={selectedOption}
        noOptionsMessage={() => (
          <>
            No locations found.{' '}
            <Link to={ROUTES.addLocation()}>Click here to add a location.</Link>
          </>
        )}
        formatOptionLabel={(option, meta) => (
          <HighlightableContent
            searchText={meta.inputValue}
            highlightableItems={{ label: option.label }}
            matchingElementsClassName="bg-blue-100 p-px text-blue-800"
          >
            {({ highlightableItems }) => (
              <SanitizedHtml html={highlightableItems.label} as="span" />
            )}
          </HighlightableContent>
        )}
        onChange={handleChange}
        styles={{
          container: (provided) => ({
            ...provided,
            minWidth: '180px',
            width: '100%',
          }),
          option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
              ? provided.backgroundColor
              : 'transparent',
            color: state.isSelected ? provided.color : 'inherit',
            ':hover': {
              backgroundColor: state.isSelected
                ? undefined
                : getCssVariable('--color-slate-100'),
            },
          }),
        }}
      />
      <span className="mx-2 flex min-w-6">
        <MutationIcon state={mutationIconState} />
      </span>
    </div>
  )
}

export default SelectLocation
